// ================================================================
// 🚚 AFTERPAY REPORT - Relatório de Logística e Entregas
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import { useChartsColors } from '../../hooks/useChartsColors';
import '../../components/reports/Reports.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

const AfterPayReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [afterpayData, setAfterPayData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsColors = useChartsColors();
  const loadAfterPayReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🚚 Carregando Relatório AfterPay com filtros...', filtersToUse);

      const result = await ReportsService.getAfterPayReport({
        period: filtersToUse.period,
        product_id: filtersToUse.advancedFilters?.product_id,
        sale_status: filtersToUse.advancedFilters?.sale_status
      });

      if (result.success) {
        setAfterPayData(result.data);
        console.log('✅ Relatório AfterPay carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAfterPayReport = useCallback(async () => {
    loadAfterPayReportWithFilters(filters);
  }, [filters, loadAfterPayReportWithFilters]);

  useEffect(() => {
    loadAfterPayReport();
  }, [loadAfterPayReport]);

  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => {
      const newFilters = { ...prev, period: newPeriod };
      setTimeout(() => loadAfterPayReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev, advancedFilters };
      setTimeout(() => loadAfterPayReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const ordersByStatusData = afterpayData?.charts?.ordersByStatus ? {
    labels: afterpayData.charts.ordersByStatus.map(item => {
      const statusMap = {
        'delivered': 'Entregue',
        'pending': 'Pendente',
        'in_transit': 'Em Trânsito',
        'cancelled': 'Cancelado'
      };
      return statusMap[item.status] || item.status;
    }),
    datasets: [{
      label: 'Pedidos por Status',
      data: afterpayData.charts.ordersByStatus.map(item => item.count),
      backgroundColor: ['#28a745', '#ffc107', '#007bff', '#dc3545'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  } : { labels: [], datasets: [] };

  const deliveryTrendData = afterpayData?.charts?.deliveryTrend ? {
    labels: afterpayData.charts.deliveryTrend.map(item => new Date(item.date).toLocaleDateString('pt-BR')),
    datasets: [
      {
        label: 'Total de Pedidos',
        data: afterpayData.charts.deliveryTrend.map(item => item.total_orders),
        borderColor: '#007bff',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        tension: 0.4
      },
      {
        label: 'Entregues',
        data: afterpayData.charts.deliveryTrend.map(item => item.delivered),
        borderColor: '#28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        tension: 0.4
      },
      {
        label: 'Pendentes',
        data: afterpayData.charts.deliveryTrend.map(item => item.pending),
        borderColor: '#ffc107',
        backgroundColor: 'rgba(255, 193, 7, 0.1)',
        tension: 0.4
      }
    ]
  } : { labels: [], datasets: [] };

  const ordersColumns = [
    {
      key: 'tracking_code',
      label: 'Código de Rastreio',
      sortable: true,
      render: (value) => <strong>{value || 'N/A'}</strong>
    },
    {
      key: 'sale_id',
      label: 'ID Venda',
      sortable: true
    },
    {
      key: 'product_name',
      label: 'Produto',
      sortable: true
    },
    {
      key: 'customer_name',
      label: 'Cliente',
      sortable: true
    },
    {
      key: 'delivery_status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusMap = {
          'delivered': { label: 'Entregue', color: '#28a745' },
          'pending': { label: 'Pendente', color: '#ffc107' },
          'in_transit': { label: 'Em Trânsito', color: '#007bff' },
          'cancelled': { label: 'Cancelado', color: '#dc3545' }
        };
        const status = statusMap[value] || { label: value, color: '#6c757d' };
        return (
          <span style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            backgroundColor: `${status.color}20`,
            color: status.color
          }}>
            {status.label}
          </span>
        );
      }
    },
    {
      key: 'total_amount_formatted',
      label: 'Valor',
      sortable: true
    },
    {
      key: 'created_at',
      label: 'Data do Pedido',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : 'N/A'
    },
    {
      key: 'delivered_at',
      label: 'Data de Entrega',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString('pt-BR') : '-'
    }
  ];

  return (
    <MasterLayout>
      <Container fluid className="reports-container" style={{ padding: '24px', maxWidth: '100%' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon icon="mdi:truck-delivery" style={{ fontSize: '22px', color: '#007bff' }} />
                <span className='text-contrast' style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                  Relatório AfterPay / Logística
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowFilterDrawer(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:filter-variant" style={{ fontSize: '20px' }} />
                  Filtros Avançados
                  {Object.keys(filters.advancedFilters).length > 0 && (
                    <span style={{
                      backgroundColor: '#007bff',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {Object.keys(filters.advancedFilters).length}
                    </span>
                  )}
                </Button>
                <Button
                  variant="outline-success"
                  onClick={() => console.log('Exportar CSV')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:file-excel" style={{ fontSize: '20px' }} />
                  Exportar CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadAfterPayReport}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                >
                  <Icon icon="mdi:refresh" style={{ fontSize: '20px' }} />
                  Atualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Bar */}
        <FilterBar
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onApplyAdvancedFilters={handleAdvancedFilters}
          onRefresh={loadAfterPayReport}
          loading={loading}
          advancedFilters={filters.advancedFilters}
          onUserFilterChange={() => {}}
        />

        <hr style={{ margin: '24px 0', borderTop: '2px solid #dee2e6' }} />

        {/* Error State */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Carregando dados de logística...</p>
          </div>
        )}

        {/* Main Content */}
        {!loading && afterpayData && (
          <>
            {/* KPIs */}
            <Row className="mb-4">
              <Col xs={12} sm={6} lg={4} xl={2} className="mb-3">
                <StatCard
                  title={afterpayData.kpis.total_orders.label}
                  value={afterpayData.kpis.total_orders.value}
                  icon="mdi:package-variant-closed"
                  color="#007bff"
                />
              </Col>
              <Col xs={12} sm={6} lg={4} xl={2} className="mb-3">
                <StatCard
                  title={afterpayData.kpis.delivered_orders.label}
                  value={afterpayData.kpis.delivered_orders.value}
                  icon="mdi:check-circle"
                  color="#28a745"
                />
              </Col>
              <Col xs={12} sm={6} lg={4} xl={2} className="mb-3">
                <StatCard
                  title={afterpayData.kpis.pending_orders.label}
                  value={afterpayData.kpis.pending_orders.value}
                  icon="mdi:clock-outline"
                  color="#ffc107"
                />
              </Col>
              <Col xs={12} sm={6} lg={4} xl={2} className="mb-3">
                <StatCard
                  title={afterpayData.kpis.in_transit_orders.label}
                  value={afterpayData.kpis.in_transit_orders.value}
                  icon="mdi:truck-fast"
                  color="#17a2b8"
                />
              </Col>
              <Col xs={12} sm={6} lg={4} xl={2} className="mb-3">
                <StatCard
                  title={afterpayData.kpis.total_value.label}
                  value={afterpayData.kpis.total_value.formatted}
                  icon="mdi:currency-usd"
                  color="#6f42c1"
                />
              </Col>
              <Col xs={12} sm={6} lg={4} xl={2} className="mb-3">
                <StatCard
                  title={afterpayData.kpis.avg_order_value.label}
                  value={afterpayData.kpis.avg_order_value.formatted}
                  icon="mdi:chart-line"
                  color="#fd7e14"
                />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
              <Col md={6} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '20px', fontWeight: '600', display: 'block', marginBottom: '20px' }}>
                      Pedidos por Status
                    </span>
                    <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Doughnut data={ordersByStatusData} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Tendência de Entregas
                    </span>
                    <Line data={deliveryTrendData} options={{ responsive: true, maintainAspectRatio: true, scales: { x: { grid: { color: chartsColors } }, y: { grid: { color: chartsColors }}}}} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Orders Table */}
            <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                  Lista de Pedidos
                </span>
                <SmartTable
                  columns={ordersColumns}
                  data={afterpayData.ordersList || []}
                  searchable={true}
                  pagination={true}
                  itemsPerPage={10}
                />
              </Card.Body>
            </Card>
          </>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          show={showFilterDrawer}
          onHide={() => setShowFilterDrawer(false)}
          onApply={(filters) => {
            setShowFilterDrawer(false);
            handleAdvancedFilters(filters);
          }}
          currentFilters={filters.advancedFilters}
        />
      </Container>
    </MasterLayout>
  );
};

export default AfterPayReport;

